import { CademyError } from '@shared/domain-shared';
import { QueryFunction, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

type Slot = {
    from: Date;
    to: Date;
};

type AvailabilitySlotsArguments = {
    educatorSlug: string;
    courseSlug: string;
    from?: Date;
    to?: Date;
    durationInSeconds: number;
};

const getAvailabilitySlots = async ({
    educatorSlug,
    courseSlug,
    from,
    to,
    durationInSeconds,
}: AvailabilitySlotsArguments): Promise<Slot[]> => {
    if (!from || !to) {
        return [];
    }
    const response = await fetch(
        `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/courses/${educatorSlug}/${courseSlug}/availability/slots?from=${from.toISOString()}&to=${to.toISOString()}&duration=${durationInSeconds}`
    );

    if (!response.ok) {
        if (response.headers.get('content-type') === 'application/problem+json') {
            const problemDetails = await response.json();
            const error = CademyError.fromObject(problemDetails);
            throw error;
        }
        const error = await response.text();
        throw new Error(error);
    }

    const dates = await response.json();
    return dates.map((slot: { from: string; to: string }) => {
        return {
            from: new Date(slot.from),
            to: new Date(slot.to),
        };
    });
};

type QueryKey = [
    queryType: 'GET /courses/{educatorSlug/{courseSlug/availability/slots',
    educatorSlug: string,
    courseSlug: string,
    from: Date | undefined,
    to: Date | undefined,
    durationInSeconds: number,
];
const availabilitySlotsQueryFunction: QueryFunction<Slot[], QueryKey> = ({ queryKey }) => {
    const availabilitySlotsArguments: AvailabilitySlotsArguments = {
        educatorSlug: queryKey[1],
        courseSlug: queryKey[2],
        from: queryKey[3],
        to: queryKey[4],
        durationInSeconds: queryKey[5],
    };
    return getAvailabilitySlots(availabilitySlotsArguments);
};

export type UseAvailabilitySlotsOptions = Omit<
    UseQueryOptions<Slot[], unknown, Slot[], QueryKey>,
    'queryKey' | 'queryFn'
>;
export const useAvailabilitySlots = (
    availabilitySlotsArgs: AvailabilitySlotsArguments,
    options?: UseAvailabilitySlotsOptions
) => {
    const queryKey = useMemo((): QueryKey => {
        return [
            'GET /courses/{educatorSlug/{courseSlug/availability/slots',
            availabilitySlotsArgs.educatorSlug,
            availabilitySlotsArgs.courseSlug,
            availabilitySlotsArgs.from,
            availabilitySlotsArgs.to,
            availabilitySlotsArgs.durationInSeconds,
        ];
    }, [availabilitySlotsArgs]);

    return useQuery({
        queryKey,
        queryFn: availabilitySlotsQueryFunction,
        ...options,
    });
};
